/**
 * Frontline admin — wire shapes for the tenant frontline console
 * (consumes /api/tenant/v1/frontline/*, the existing audited admin
 * handler). Closes the "backend exists, frontend never calls it"
 * gap. Zod is the single source of truth.
 */

import { z } from "zod";

export const pinPolicySchema = z.object({
  length: z.number(),
  rotation_days: z.number(),
  lockout_threshold: z.number(),
  lockout_minutes: z.number(),
  min_unique_digits: z.number(),
  station_spray_threshold: z.number().optional(),
  station_spray_window_minutes: z.number().optional(),
  station_quarantine_minutes: z.number().optional(),
});

export const sessionPolicySchema = z.object({
  inactivity_minutes: z.number(),
  // #3 walk-away: 0 = disabled (idle alone governs). A stricter
  // unattended-station ceiling; the bearer token is clamped to it
  // server-side.
  walk_away_minutes: z.number(),
  replace_on_next_scan: z.boolean(),
  max_session_minutes: z.number(),
});

export const frontlineConfigSchema = z.object({
  frontline_enabled: z.boolean(),
  pin_method_enabled: z.boolean(),
  nfc_method_enabled: z.boolean(),
  station_pairing_required: z.boolean(),
  break_glass_enabled: z.boolean(),
  kiosk_pass_enabled: z.boolean(),
  pin_policy: pinPolicySchema,
  session_policy: sessionPolicySchema,
});
export type FrontlineConfig = z.infer<typeof frontlineConfigSchema>;

export const stationSchema = z.object({
  product_area:z.enum(["badge_sign_in","kiosk_pass"]),
  id: z.string(),
  station_name: z.string(),
  location_label: z.string().optional().nullable(),
  status: z.string(),
  failed_attempt_count: z.number().optional().default(0),
  quarantined_until: z.string().optional().nullable(),
  last_seen_at: z.string().optional().nullable(),
  paired_at: z.string().optional().nullable(),
});
export type Station = z.infer<typeof stationSchema>;

export const stationsResponseSchema = z.object({
  stations: z.array(stationSchema).nullable().default([]),
});

export const pairStationResponseSchema = z.object({
  station: stationSchema,
  pairing_secret: z.string(),
});
export type PairStationResponse = z.infer<typeof pairStationResponseSchema>;

export const shiftLeadResponseSchema = z.object({
  user_id: z.string(),
  is_shift_lead: z.boolean(),
});

export const frontlineGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  is_frontline: z.boolean(),
  member_count: z.number().optional().default(0),
});
export type FrontlineGroup = z.infer<typeof frontlineGroupSchema>;

export const frontlineGroupsResponseSchema = z.object({
  groups: z.array(frontlineGroupSchema).nullable().default([]),
});
